/* eslint-disable no-unused-vars */
import { useEffect } from "react";
import MainLayout from "../../../layouts/MainLayout";
import { useNavigate, useParams } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import useForm from "../../../hooks/useForm";
import {
  fixZeroNumber,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import { detailsEquipmentFields } from "../../../constants/forms/equipmentFields";
import DetailsSectionRenderer from "../../../components/DetailsSectionRenderer";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import TableWithInactiveFilter from "../../../components/table/TableWithInactiveFilter";
import useUrlFilters from "../../../hooks/useUrlFilters";
import useGenericFilter from "../../../hooks/filters/useGenericFilter";
import { usePermissions } from "../../../hooks/usePermissions";
import ListHeader from "../../../components/ListHeader";
import { actionsMoviments, columnsMoviments } from "./config-sectios/movements";
import useFilterHandlers from "../../../hooks/useFilterHandlers";
import {
  actionsMaintenances,
  collapseConfigMaintenances,
  columnsMaintenances,
} from "./config-sectios/mantenance";
import {
  columnsUpgrades,
  statusLabels,
  typeLabels,
} from "./config-sectios/upgrades";
import { formatDateFront, formatDateOnlyFront } from "../../../utils/formatDate";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";
import AccordionControlled from "../../../components/AccordionControlled";
import { Box } from "@mui/material";
import { EquipmentEnum } from "../../../enums/equipment-enum";

const RelatedMovementHistory = ({ equipmentId, navigate, canAccess }) => {
  const { get: baseServiceGet } = useBaseService(navigate);

  // Filtros locais para cada tabela interna
  const { filters, pagination, changePage, setPagination } = useUrlFilters(
    { deleted_at: false },
    `related_mov_${equipmentId}`,
  );

  const {
    data,
    isLoading: isLoadingMovementRelated,
    isFetching: isFetchingMovementRelated,
  } = useQueryWithLoader({
    queryKey: ["equipment_movements", equipmentId, filters, pagination],
    queryFn: async () => {
      const params = {
        filters: transformFiltersForUrl(filters),
        page: pagination.page,
        perPage: pagination.perPage,
        sort: { column: "id", order: "desc" },
      };
      const response = await baseServiceGet(
        entities.equipments.movements.get(equipmentId),
        params,
      );
      return response.result;
    },
    select: (data) => ({
      movements: data.data?.map((item) => ({
        id: item.id,
        number: item.movement?.number ?? "-",
        movemente_id: item.movement_id,
        movement_date:
          formatDateFront({ rawDate: item.movement?.movement_date }) ?? "-",
        customer: item.movement?.recipient_customer?.name ?? "-",
        type: item.type?.name ?? "-",
        movement: item.movement,
      })),
      totalPages: data?.last_page,
    }),
  });

  return (
    <TableWithInactiveFilter
      data={data?.movements || []}
      columns={columnsMoviments}
      actions={actionsMoviments(navigate, canAccess)}
      totalPages={data?.totalPages || 1}
      currentPage={pagination.page - 1}
      onPageChange={changePage}
      rowsPerPage={pagination.perPage}
      onRowsPerPageChange={(e) =>
        setPagination({ page: 1, perPage: +e.target.value })
      }
      type="default"
      isLoading={isLoadingMovementRelated}
      isFetching={isFetchingMovementRelated}
    />
  );
};

const DetailsEquipmentPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { canAccess } = usePermissions();
  const { formData, setFormData } = useForm(
    setDefaultFieldValues(detailsEquipmentFields),
  );
  const { get: baseServiceGet, getByColumn: baseServiceGetByColumn } =
    useBaseService(navigate);

  const { data: equipmentData } = useQueryWithLoader({
    queryKey: ["equipment", id],
    queryFn: async () => {
      const response = await baseServiceGetByColumn(
        entities.equipments.getById(id),
      );
      return response.result;
    },
    select: (data) => {
      return {
        equipment: {
          name: data.name ?? "-",
          number: data.number ?? "-",
          serial_number: data.serial_number ?? "-",
          completed_by: data.completed_by?.name ?? "-",
          serial_required: data.serial_required ? "Sim" : ("Não" ?? "-"),
          status: data.status?.name ?? "-",
          current_customer:
            [EquipmentEnum.RESERVED, EquipmentEnum.RENTED].includes(Number(data.status_id))
              ? data.current_customer?.alias ?? "-"
              : "-",
          destination_address: data.address?.formatted_address ?? "-",
          created_at: formatDateFront({ rawDate: data.created_at }) ?? "-",
          purchase_date:
            formatDateOnlyFront({
              rawDate: data.creation_transaction?.purchase_date,
            }) || "-",
          warranty_date: data.creation_transaction_item?.warranty_date ?? "-",
          current_organization: data.organization?.name ?? "-",
          owner_organization: data.creation_transaction?.buyer?.name ?? "-",
          doc_number: data.creation_transaction?.doc_number ?? "-",
          supplier: data.creation_transaction?.supplier?.name ?? "-",
          category: data.creation_transaction_item?.category?.name ?? "-",
          condition: data.creation_transaction_item?.condition?.name ?? "-",
          length: fixZeroNumber(data.product_item?.product?.length) ?? "-",
          width: fixZeroNumber(data.product_item?.product?.width) ?? "-",
          height: fixZeroNumber(data.product_item?.product?.height) ?? "-",
          weight: fixZeroNumber(data.product_item?.product?.weight) ?? "-",
        },
      };
    },
  });

  // * Filtros para movimentações
  const {
    filters: filtersMovements,
    setFilters: applyUrlFiltersMovements,
    pagination: paginationMovements,
    changePage: changePageMovements,
    setPagination: setPaginationMovements,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    "equipment_movements",
  );

  const {
    handleFilterApply: handleFilterApplyMovements,
    handleFilterClear: handleFilterClearMovements,
  } = useFilterHandlers(applyUrlFiltersMovements);

  const { localFilterValues: localFilterValuesMovements } = useGenericFilter(
    {
      deleted_at: false,
    },
    handleFilterApplyMovements,
    handleFilterClearMovements,
  );

  const {
    data: movementsData,
    isLoading: isLoadingMovements,
    isFetching: isFetchingMovements,
  } = useQueryWithLoader({
    queryKey: [
      "equipment_movements",
      id,
      filtersMovements,
      paginationMovements,
    ],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filtersMovements);
      const params = {
        filters: filtersTransformed,
        page: paginationMovements.page,
        perPage: paginationMovements.perPage,
        sort: { column: "created_at", order: "desc" },
      };
      const response = await baseServiceGet(
        entities.equipments.movements.get(id),
        params,
      );
      return response.result;
    },
    enabled: !!id,
    keepPreviousData: true,
    refetchOnWindowFocus: true,
    select: (data) => ({
      movements: data.data?.map((item) => ({
        id: item.id,
        number: item.number ?? "-",
        movemente_id: item.id,
        movement_date: formatDateFront({ rawDate: item.movement_date }) ?? "-",
        customer: item.recipient_customer?.alias ?? "-",
        type: item.movement_type?.name ?? "-",
        movement: item,
      })),
      totalPages: data?.last_page,
    }),
  });

  const { movements = [], totalPages: totalPagesMovements = 1 } =
    movementsData || {};

  // * Filtro para equipamentos antigos atrelados

  const {
    filters: filtersRelateds,
    setFilters: applyUrlFiltersRelateds,
    pagination: paginationRelateds,
    changePage: changePageRelateds,
    setPagination: setPaginationRelateds,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    "relateds_equipments",
  );

  const {
    handleFilterApply: handleFilterApplyRelateds,
    handleFilterClear: handleFilterClearRelateds,
  } = useFilterHandlers(applyUrlFiltersRelateds);

  const { localFilterValues: localFilterValuesRelateds } = useGenericFilter(
    {
      deleted_at: false,
    },
    handleFilterApplyRelateds,
    handleFilterClearRelateds,
  );

  const { data: relatedsData } = useQueryWithLoader({
    queryKey: ["relateds_equipments", id, filtersRelateds, paginationRelateds],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filtersRelateds);
      const params = {
        filters: filtersTransformed,
        page: paginationRelateds.page,
        perPage: paginationRelateds.perPage,
      };
      const response = await baseServiceGet(
        entities.equipments.old.get(id),
        params,
      );
      return response.result;
    },
    enabled: !!id,
    select: (data) => ({
      relateds: data.data?.map((equipment) => ({
        id: equipment.id,
        name: `${equipment.number} - ${equipment.name}`,
      })),
      totalPages: data?.last_page,
    }),
  });

  const { relateds = [], totalPages: totalPagesRelateds = 1 } =
    relatedsData || {};

  // * Filtros para manutenções
  const {
    filters: filtersMaintenance,
    setFilters: applyUrlFiltersMaintenance,
    pagination: paginationMaintenance,
    changePage: changePageMaintenance,
    setPagination: setPaginationMaintenance,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    "equipment_maintenance",
  );

  const {
    handleFilterApply: handleFilterApplyMaintenance,
    handleFilterClear: handleFilterClearMaintenance,
  } = useFilterHandlers(applyUrlFiltersMaintenance);

  const { localFilterValues: localFilterValuesMaintenance } = useGenericFilter(
    {
      deleted_at: false,
    },
    handleFilterApplyMaintenance,
    handleFilterClearMaintenance,
  );

  const {
    data: maintenancesData,
    isLoading: isLoadingMaintenances,
    isFetching: isFetchingMaintenances,
  } = useQueryWithLoader({
    queryKey: [
      "equipment_maintenances",
      id,
      filtersMaintenance,
      paginationMaintenance,
    ],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filtersMaintenance);
      const params = {
        filters: filtersTransformed,
        page: paginationMaintenance.page,
        perPage: paginationMaintenance.perPage,
        sort: { column: "created_at", order: "desc" },
      };
      const response = await baseServiceGet(
        entities.equipments.maintenances.get(id),
        params,
      );
      return response.result;
    },
    enabled: !!id,
    keepPreviousData: true,
    refetchOnWindowFocus: true,
    select: (data) => {
      return {
        maintenances: data.data.map((maintenance) => {
          const sortedItems = [...(maintenance.items || [])].sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at),
          );
          const lastItem = sortedItems[0];

          return {
            id: maintenance.id,
            maintenance_date: formatDateFront({
              rawDate: maintenance.created_at,
            }),
            // Agora os dados abaixo serão relativos ao último estágio desta manutenção
            maintenance_status: lastItem?.maintenance_status?.name || "N/A",
            technician: lastItem?.technician?.name || "Não atribuído",
            technician_status: lastItem?.technician_status?.name || "N/A",
            maintenance_type:
              lastItem?.maintenance_type?.name ||
              maintenance.maintenance_type?.name,

            maintenance_details: maintenance.details || "-",
            isDeleted: !!maintenance.deleted_at,
            isClosed: !!maintenance.is_closed, // Útil para mostrar cadeado/ícone de fechado

            items: sortedItems?.map((service) => ({
              id: service.id,
              service_date: formatDateFront({ rawDate: service.created_at }),
              technician_status: service.technician_status?.name,
              details: service.details,
            })),
          };
        }),
        totalPages: data?.last_page,
      };
    },
  });

  const { maintenances = [], totalPages: totalPagesMaintenances = 1 } =
    maintenancesData || {};

  // * Histórico de upgrade/downgrade
  const {
    filters: filtersUpgrades,
    pagination: paginationUpgrades,
    changePage: changePageUpgrades,
    setPagination: setPaginationUpgrades,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    "equipment_upgrades",
  );

  const {
    data: upgradesData,
    isLoading: isLoadingUpgrades,
    isFetching: isFetchingUpgrades,
  } = useQueryWithLoader({
    queryKey: ["equipment_upgrades", id, filtersUpgrades, paginationUpgrades],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filtersUpgrades);
      const params = {
        filters: filtersTransformed,
        page: paginationUpgrades.page,
        perPage: paginationUpgrades.perPage,
        sort: { column: "created_at", order: "desc" },
      };
      const response = await baseServiceGet(
        entities.equipments.upgrades.get(id),
        params,
      );
      return response.result;
    },
    enabled: !!id,
    keepPreviousData: true,
    refetchOnWindowFocus: true,
    select: (data) => ({
      upgrades: data.data?.map((upgrade) => ({
        id: upgrade.id,
        created_at: formatDateFront({ rawDate: upgrade.created_at }) ?? "-",
        type: upgrade.type,
        type_label: typeLabels[upgrade.type] ?? "-",
        before_group: upgrade.before_group?.name ?? "-",
        after_group: upgrade.after_group?.name ?? "-",
        status: upgrade.status,
        status_label: statusLabels[upgrade.status] ?? upgrade.status,
        requested_by: upgrade.requested_by?.name ?? "-",
        approved_by: upgrade.approved_by?.name ?? "-",
        reason: upgrade.reason ?? "-",
        isDeleted: !!upgrade.deleted_at,
      })),
      totalPages: data?.last_page,
    }),
  });

  const { upgrades = [], totalPages: totalPagesUpgrades = 1 } =
    upgradesData || {};

  useEffect(() => {
    if (equipmentData) {
      setFormData({
        equipment: equipmentData.equipment,
      });
    }
  }, [equipmentData, setFormData]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title={`Detalhes do Equipamento: ${
          equipmentData?.equipment?.name || ""
        }`}
        showBackButton={true}
        backUrl="/aluguel/equipamentos/"
      />

      <div className="container-fluid p-1">
        <DetailsSectionRenderer
          formData={formData}
          sections={detailsEquipmentFields}
          readOnly
        />

        {/* Histórico de Movimentações */}
        <div className="mt-4">
          <ListHeader
            title="Histórico de Movimentações"
            buttonIsNotVisible={true}
            permission={"Listar movimentos"}
            canAccess={canAccess}
          />
          <TableWithInactiveFilter
            data={movements || []}
            columns={columnsMoviments}
            actions={actionsMoviments(navigate, canAccess)}
            totalPages={totalPagesMovements}
            currentPage={paginationMovements.page - 1}
            onPageChange={changePageMovements}
            rowsPerPage={paginationMovements.perPage}
            onRowsPerPageChange={(event) => {
              const newPerPage = +event.target.value;
              setPaginationMovements({ page: 1, perPage: newPerPage });
            }}
            filterValues={localFilterValuesMovements}
            localFilterValues={localFilterValuesMovements}
            applyUrlFilters={applyUrlFiltersMovements}
            type="default"
            isLoading={isLoadingMovements}
            isFetching={isFetchingMovements}
          />
        </div>

        {/* Equipamentos antigos atrelados */}
        <div className="mt-4">
          {relateds.length > 0 ? (
            relateds.map((related) => (
              <Box key={related.id}>
                <ListHeader
                  title="Equipamentos antigos atrelados"
                  buttonIsNotVisible={true}
                  permission={"Listar equipamentos"}
                  canAccess={canAccess}
                />
                <Box sx={{ mb: 2, mt: 6 }}>
                  <AccordionControlled titleButton={related.name}>
                    <div className="pb-2">
                      <ListHeader
                        title={`Histórico de Movimentações`}
                        buttonIsNotVisible={true}
                        permission={"Listar movimentos"}
                        canAccess={canAccess}
                      />

                      <RelatedMovementHistory
                        equipmentId={related.id}
                        navigate={navigate}
                        canAccess={canAccess}
                      />
                    </div>
                  </AccordionControlled>
                </Box>
              </Box>
            ))
          ) : (
            <></>
          )}
        </div>

        {/* Histórico de Manutenções */}
        <div className="mt-4">
          <ListHeader
            title="Histórico de Manutenções"
            buttonIsNotVisible={true}
            permission={"Listar manutenções"}
            canAccess={canAccess}
          />
          <TableWithInactiveFilter
            data={maintenances || []}
            columns={columnsMaintenances}
            collapseConfig={collapseConfigMaintenances}
            actions={actionsMaintenances(navigate, canAccess)}
            totalPages={totalPagesMaintenances}
            currentPage={paginationMaintenance.page - 1}
            onPageChange={changePageMaintenance}
            rowsPerPage={paginationMaintenance.perPage}
            onRowsPerPageChange={(event) => {
              const newPerPage = +event.target.value;
              setPaginationMaintenance({ page: 1, perPage: newPerPage });
            }}
            filterValues={localFilterValuesMaintenance}
            localFilterValues={localFilterValuesMaintenance}
            applyUrlFilters={applyUrlFiltersMaintenance}
            type="collapse"
            isLoading={isLoadingMaintenances}
            isFetching={isFetchingMaintenances}
          />
        </div>

        {/* Histórico de Upgrade/Downgrade */}
        <div className="mt-4">
          <ListHeader
            title="Histórico de Upgrade/Downgrade"
            buttonIsNotVisible={true}
            permission={"Listar equipamentos"}
            canAccess={canAccess}
          />
          <TableWithInactiveFilter
            data={upgrades || []}
            columns={columnsUpgrades}
            totalPages={totalPagesUpgrades}
            currentPage={paginationUpgrades.page - 1}
            onPageChange={changePageUpgrades}
            rowsPerPage={paginationUpgrades.perPage}
            onRowsPerPageChange={(event) => {
              const newPerPage = +event.target.value;
              setPaginationUpgrades({ page: 1, perPage: newPerPage });
            }}
            type="default"
            isLoading={isLoadingUpgrades}
            isFetching={isFetchingUpgrades}
          />
        </div>
      </div>
    </MainLayout>
  );
};

export default DetailsEquipmentPage;
